import { useState, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../../hooks/useAuth';
import { authClient } from '../../lib/auth-client';
import { useSubscription } from '../../hooks/useSubscription';
import { SettingsLayout } from './SettingsLayout';
import { Button } from '../../components/ui/button';
import { EditableTitle } from '../../components/ui/editable-title';
import { API_URL } from '../../lib/api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { GradientBorder } from '../../components/ui/gradient-border';
import { getGravatarUrl } from '../../lib/gravatar';
import { getPlanBadgeStyle } from '../../lib/usage';

const DELETE_CONFIRM_PHRASE = 'delete my account';

interface LinkedAccount {
  id: string;
  providerId: string;
  accountId: string;
}

const PROVIDERS = [
  { id: 'google', name: 'Google' },
  { id: 'github', name: 'GitHub' },
] as const;

type ProviderId = (typeof PROVIDERS)[number]['id'];

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
    </svg>
  );
}

function ProviderIcon({ providerId, className }: { providerId: string; className?: string }) {
  switch (providerId) {
    case 'google': return <GoogleIcon className={className} />;
    case 'github': return <GitHubIcon className={className} />;
    default: return null;
  }
}

export function SettingsAccountPage() {
  const { user } = useAuth();
  const { planType, plans } = useSubscription();
  const currentPlan = plans.find((p) => p.type === planType);
  const planName = currentPlan?.name ?? 'Free';
  const planBadgeStyle = getPlanBadgeStyle(planType);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const [error, setError] = useState('');

  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [unlinking, setUnlinking] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleRequestDeletion = useCallback(async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const response = await fetch(`${API_URL}/api/v2/cloud/account/deletion`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json().catch(() => null);
      if (response.status === 409) {
        setDeleteError(
          'You have an active subscription. Cancel it on the Billing page first, then request deletion.',
        );
        return;
      }
      if (!response.ok) {
        throw new Error(
          data?.message ?? 'Failed to request account deletion',
        );
      }
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      window.location.assign('/login');
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to request account deletion',
      );
    } finally {
      setDeleteBusy(false);
    }
  }, []);

  const fetchLinkedAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/list-accounts`, {
        credentials: 'include',
      });
      if (!response.ok) return;
      const data = await response.json();
      setLinkedAccounts(data ?? []);
    } catch {
      // silently fail — accounts list is non-critical
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinkedAccounts();
  }, [fetchLinkedAccounts]);

  useEffect(() => {
    const email = user?.email;
    if (!email) return;
    getGravatarUrl(email).then(setAvatarUrl);
  }, [user?.email]);

  const handleNameSave = useCallback(async (newName: string) => {
    setError('');
    try {
      await authClient.updateUser({ name: newName });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update name');
    }
  }, []);

  const isProviderLinked = (providerId: string) =>
    linkedAccounts.some((a) => a.providerId === providerId);

  const handleConnectProvider = async (providerId: ProviderId) => {
    setError('');
    try {
      await authClient.linkSocial({
        provider: providerId,
        callbackURL: `${window.location.origin}/settings/account`,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to connect ${providerId}. Please try again.`,
      );
    }
  };

  const handleDisconnectProvider = async (providerId: string) => {
    // Must keep at least 1 connected provider (social-only auth)
    if (linkedAccounts.length <= 1) {
      setError('You must have at least one connected account to sign in.');
      return;
    }

    setUnlinking(providerId);
    try {
      const response = await fetch(`${API_URL}/api/auth/unlink-account`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId }),
      });
      if (!response.ok) {
        throw new Error('Failed to disconnect provider');
      }
      await fetchLinkedAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect provider');
    } finally {
      setUnlinking(null);
    }
  };

  const initials = (user?.name || user?.email || 'U').charAt(0).toUpperCase();
  const imageUrl = !avatarFailed ? (user?.image || avatarUrl) : null;

  return (
    <SettingsLayout activeTab="account">
      <div className="space-y-8">
        <h1 className="text-2xl font-semibold text-white">Account</h1>

        {/* Profile card */}
        <GradientBorder>
          <section className="p-5">
            <div className="flex items-center gap-4">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={user?.name || user?.email || 'User'}
                  className="w-14 h-14 rounded-full border border-white/10"
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-neutral-700 border border-white/10 flex items-center justify-center text-xl font-medium text-white">
                  {initials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <EditableTitle
                  title={user?.name || 'No name set'}
                  onSave={handleNameSave}
                  className="text-white font-medium"
                />
                <p className="text-sm text-white/50 mt-0.5">{user?.email}</p>
              </div>
              <Link
                to="/settings/billing"
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold uppercase tracking-wider transition-opacity hover:opacity-80 ${planBadgeStyle}`}
              >
                {planName}
              </Link>
            </div>

          </section>
        </GradientBorder>

        {/* Authentication section */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-1">
            Connected Accounts
          </h2>
          <p className="text-sm text-white/50 mb-6">
            Manage how you sign in to your account.
          </p>

          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300 mb-4">
              {error}
            </div>
          )}

          <div className="space-y-3">
            {accountsLoading ? (
              <div className="text-sm text-white/50 py-2">Loading...</div>
            ) : (
              PROVIDERS.map((provider) => {
                const linked = isProviderLinked(provider.id);
                const isUnlinking = unlinking === provider.id;

                return (
                  <GradientBorder key={provider.id} className="rounded-lg" innerClassName="rounded-[7px] bg-neutral-900">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <ProviderIcon
                          providerId={provider.id}
                          className="h-5 w-5 text-white/60"
                        />
                        <div>
                          <p className="text-sm font-medium text-white">
                            {provider.name}
                          </p>
                          {linked && (
                            <p className="text-xs text-white/50">Connected</p>
                          )}
                        </div>
                      </div>

                      {linked ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDisconnectProvider(provider.id)}
                          disabled={isUnlinking}
                        >
                          {isUnlinking ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleConnectProvider(provider.id)}
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </GradientBorder>
                );
              })
            )}
          </div>
        </section>

        {/* Danger zone */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-1">
            Delete Account
          </h2>
          <p className="text-sm text-white/50 mb-6">
            Permanently remove your account and everything in it.
          </p>

          {deleteError && !deleteDialogOpen && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300 mb-4">
              {deleteError}
            </div>
          )}

          <GradientBorder
            className="rounded-lg"
            innerClassName="rounded-[7px] bg-neutral-900"
          >
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">
                  Delete this account
                </p>
                <p className="text-xs text-white/50 mt-0.5">
                  Deletes your sites, conversations, images, and account data.
                  This cannot be undone.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-red-400 border-red-500/30 hover:bg-red-500/10 hover:text-red-300"
                onClick={() => {
                  setDeleteError('');
                  setDeleteConfirmText('');
                  setDeleteDialogOpen(true);
                }}
              >
                Delete account
              </Button>
            </div>
          </GradientBorder>
        </section>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes your account, all of your sites and
              their content, conversations, and uploaded images. This is
              permanent and cannot be reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label
              htmlFor="delete-confirm"
              className="block text-sm text-white/70 mb-2"
            >
              Type <strong>{DELETE_CONFIRM_PHRASE}</strong> to confirm
            </label>
            <input
              id="delete-confirm"
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              autoComplete="off"
              className="w-full rounded-lg bg-neutral-800 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-red-500/50"
              placeholder={DELETE_CONFIRM_PHRASE}
            />
            {deleteError && (
              <p className="text-sm text-red-300 mt-3">{deleteError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteBusy}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRequestDeletion}
              disabled={
                deleteBusy ||
                deleteConfirmText.trim().toLowerCase() !==
                  DELETE_CONFIRM_PHRASE
              }
            >
              {deleteBusy ? 'Requesting...' : 'Delete my account'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsLayout>
  );
}
